import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// @mui
import {
  Avatar,
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
// utils
import { fToNow } from '../../../utils/formatTime';
// components
import Iconify from '../../../components/iconify';
import Scrollbar from '../../../components/scrollbar';
import MenuPopover from '../../../components/menu-popover';
import { IconButtonAnimate } from '../../../components/animate';
import { FUNCTIONS, NOTIFICATION_STATUS, STATUSES } from '../../../utils/constant';
import axios from '../../../utils/axios';
import { PATH_DASHBOARD } from '../../../routes/paths';
import { useSnackbar } from '../../../components/snackbar';
import { useNotifications } from '../../../swr/notification';

// ----------------------------------------------------------------------

export default function NotificationsPopover() {
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();

  const [openPopover, setOpenPopover] = useState(null);

  const [page, setPage] = useState(1);

  const { notifications, totalNoti, countNotiNotViewed, isLoading, mutate } = useNotifications({
    rows_per_page: 10,
    page,
  });

  const [data, setData] = useState([]);

  const [countNoti, setCountNoti] = useState(0);

  const [isUpdate, setIsUpdate] = useState(false);

  useEffect(() => {
    if (!isLoading && notifications.length) {
      const newNoti = notifications;
      if (isUpdate) {
        setIsUpdate(false);
        setData((prevData) => [...prevData, ...newNoti]);
        setCountNoti((prevData) => prevData + notifications.length);
      }
    }
  }, [isLoading, isUpdate]);

  useEffect(() => {
    if (!isUpdate) {
      setData(notifications);
      setCountNoti(notifications.length);
    }
  }, [notifications]);

  const handleRead = async (noti) => {
    setOpenPopover(null);

    switch (noti?.function_id) {
      case FUNCTIONS.sale_receipt:
        navigate(PATH_DASHBOARD.salesReceipt.edit(noti?.feature_id, 'edit'));
        break;
      case FUNCTIONS.gift:
        navigate(PATH_DASHBOARD.orderGift.edit(noti?.feature_id, 'view'));
        break;
      case FUNCTIONS.party:
        navigate(PATH_DASHBOARD.parties.search(noti?.feature_id));
        break;
      case FUNCTIONS.refund:
        navigate(PATH_DASHBOARD.refundOrder.edit(noti?.feature_id));
        break;
      case FUNCTIONS.import_buy:
        navigate(PATH_DASHBOARD.importWarehouse.search(noti?.feature_id));
        break;
      case FUNCTIONS.import_orther:
        navigate(PATH_DASHBOARD.importWarehouse.search(noti?.feature_id));
        break;
      case FUNCTIONS.export_orther:
        navigate(PATH_DASHBOARD.exportWarehouse.search(noti?.feature_id));
        break;
      case FUNCTIONS.export_transfer:
        navigate(PATH_DASHBOARD.exportWarehouse.search(noti?.feature_id));
        break;
      case FUNCTIONS.blog:
        navigate(PATH_DASHBOARD.blog.view(noti?.feature_id));
        break;
      case FUNCTIONS.payment_order.id:
        navigate(PATH_DASHBOARD.paymentMultipleOrder.approval(noti?.feature_id));
        break;
      default:
    }

    await handleViewedNoti();

    try {
      await axios.post('viewed-notification', { id: noti?.id, type: 'read' });
      mutate();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  const handleOpenPopover = (event) => {
    // setOpenPopover(event.currentTarget);
  };

  const handleClosePopover = async () => {
    setOpenPopover(null);
    setPage(1);

    await handleViewedNoti();
  };

  const handleViewedNoti = async () => {
    if (countNotiNotViewed) {
      try {
        await axios.post('viewed-notification', { type: 'viewed' });
        mutate();
      } catch (error) {
        console.error(error);
        enqueueSnackbar(error.message, { variant: 'error' });
      }
    }
  };

  const getMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    setIsUpdate(true);
  };

  return (
    <>
      <IconButtonAnimate
        color={openPopover ? 'primary' : 'default'}
        onClick={handleOpenPopover}
        sx={{ width: 40, height: 40 }}
      >
        <Badge badgeContent={countNotiNotViewed} color="error">
          <Iconify icon="eva:bell-fill" />
        </Badge>
      </IconButtonAnimate>

      <MenuPopover open={openPopover} onClose={handleClosePopover} sx={{ width: 360, p: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">Thông báo</Typography>

            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {countNotiNotViewed
                ? `Bạn có ${countNotiNotViewed} thông báo chưa đọc`
                : `Bạn không có thông báo chưa đọc`}
            </Typography>
          </Box>
        </Box>

        <Scrollbar sx={{ maxHeight: 500 }}>
          <List disablePadding>
            {data.map((notification, index) => (
              <NotificationItem
                handleRead={(noti) => handleRead(noti)}
                key={`${notification?.id}_${index}`}
                notification={notification}
              />
            ))}
            <Divider sx={{ borderStyle: 'dashed' }} />

            {!isLoading && totalNoti > countNoti && (
              <Box sx={{ p: 1 }}>
                <Button fullWidth disableRipple onClick={getMore}>
                  Xem thêm
                </Button>
              </Box>
            )}

            {isLoading && (
              <div
                style={{
                  width: 'auto',
                  display: 'flex',
                  justifyContent: 'center',
                  margin: '10px',
                }}
              >
                <CircularProgress
                  style={{
                    width: 30,
                    height: 30,
                  }}
                />
              </div>
            )}
          </List>
        </Scrollbar>
        <Box sx={{ p: '3px' }} />
      </MenuPopover>
    </>
  );
}

// ----------------------------------------------------------------------

function NotificationItem({ handleRead, notification }) {
  const { avatar, title } = renderContent(notification);

  return (
    <ListItemButton
      onClick={() => handleRead(notification)}
      sx={{
        py: 1.5,
        px: 2.5,
        m: '5px',
        mt: '1px',
        mb: '3px',
        ...(notification?.is_read === NOTIFICATION_STATUS.unread && {
          bgcolor: 'action.selected',
        }),
      }}
    >
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: 'background.neutral' }}>{avatar}</Avatar>
      </ListItemAvatar>

      <ListItemText
        disableTypography
        primary={title}
        secondary={
          <Stack direction="row" sx={{ mt: 0.5, typography: 'caption', color: 'text.disabled' }}>
            <Iconify icon="eva:clock-fill" width={16} sx={{ mr: 0.5 }} />
            <Typography variant="caption">{fToNow(notification?.created_at)}</Typography>
          </Stack>
        }
      />
    </ListItemButton>
  );
}

// ----------------------------------------------------------------------

function renderContent(notification) {
  const getTitle = (noti) => {
    let title = '';
    switch (noti?.status) {
      case STATUSES.confirm_sale_receipt:
        title = `Đơn hàng bán #${noti?.title} đã được duyệt`;
        break;
      case STATUSES.reject_sale_receipt:
        title = `Đơn hàng bán #${noti?.title} bị từ chối`;
        break;
      case STATUSES.exported:
        title = `Đơn hàng bán #${noti?.title} đã được xuất kho`;
        break;
      case STATUSES.shipped:
        title = `Đơn hàng bán #${noti?.title} đã được giao`;
        break;
      case STATUSES.success_sale_receipt:
        title = `Đơn hàng bán #${noti?.title} đã hoàn thành`;
        break;
      default:
        title = `Đơn hàng bán #${noti?.title} đã được tạo`;
        if (noti?.is_update) {
          title = `Đơn hàng bán #${noti?.title} đã được cập nhật`;
        }
        break;
    }

    return title;
  };

  const getTitlePayment = (noti) => {
    if (noti?.title?.includes('ĐH')) {
      switch (noti?.status) {
        case STATUSES.approved_payment:
          return `Đơn hàng #${noti?.title} đã được duyệt phiếu thanh toán`;
        case STATUSES.decline_payment:
          return `Đơn hàng #${noti?.title} bị từ chối phiếu thanh toán`;
        default:
          return `Đơn hàng #${noti?.title} đã được tạo phiếu thanh toán`;
      }
    } else {
      switch (noti?.status) {
        case STATUSES.approved_payment:
          return `Phiếu thanh toán của khách hàng: ${noti?.title} - đã được duyệt`;

        case STATUSES.decline_payment:
          return `Phiếu thanh toán của khách hàng: ${noti?.title} - đã bị từ chối`;
        default:
          return `Phiếu thanh toán của khách hàng: ${noti?.title} - đã được tạo mới`;
      }
    }
  };

  const title = (
    <Typography variant="subtitle2">
      {(notification?.function_id === FUNCTIONS.party && `Khách hàng #${notification?.title} đã được tạo`) ||
        (notification?.function_id === FUNCTIONS.gift && `Đơn hàng tặng #${notification?.title} đã được tạo`) ||
        (notification?.function_id === FUNCTIONS.sale_receipt && `${getTitle(notification)}`) ||
        (notification?.function_id === FUNCTIONS.refund && `Đơn hàng trả #${notification?.title} đã được tạo`) ||
        ((notification?.function_id === FUNCTIONS.import_buy ||
          notification?.function_id === FUNCTIONS.import_orther) &&
          `Đơn nhập hàng #${notification?.title} đã được tạo`) ||
        ((notification?.function_id === FUNCTIONS.export_orther ||
          notification?.function_id === FUNCTIONS.export_transfer) &&
          `Đơn xuất hàng #${notification?.title} đã được tạo`) ||
        (notification?.function_id === FUNCTIONS.blog && `Tin tức mới: ${notification?.title}`) ||
        (notification?.function_id === FUNCTIONS.payment_order?.id && `${getTitlePayment(notification)}`)}
    </Typography>
  );
  if (notification?.function_id === FUNCTIONS.blog) {
    return {
      avatar: <img alt={title} src="/assets/icons/notification/ic_package.svg" />,
      title,
    };
  }
  if (notification?.function_id === FUNCTIONS.sale_receipt) {
    return {
      avatar: <img alt={title} src="/assets/icons/notification/ic_package.svg" />,
      title,
    };
  }
  if (notification?.function_id === FUNCTIONS.gift) {
    return {
      avatar: <img style={{ width: 30 }} alt={title} src="/assets/icons/notification/ic_gift.svg" />,
      title,
    };
  }
  if (notification?.function_id === FUNCTIONS.party) {
    return {
      avatar: <img style={{ width: 30 }} alt={title} src="/assets/icons/notification/ic_party.svg" />,
      title,
    };
  }
  if (notification?.function_id === FUNCTIONS.refund) {
    return {
      avatar: <img style={{ width: 30 }} alt={title} src="/assets/icons/notification/icon_refund.svg" />,
      title,
    };
  }
  if (notification?.function_id === FUNCTIONS.import_buy || notification?.function_id === FUNCTIONS.import_orther) {
    return {
      avatar: <img style={{ width: 30 }} alt={title} src="/assets/icons/notification/ic_import.svg" />,
      title,
    };
  }
  if (
    notification?.function_id === FUNCTIONS.export_orther ||
    notification?.function_id === FUNCTIONS.export_transfer
  ) {
    return {
      avatar: <img style={{ width: 30 }} alt={title} src="/assets/icons/notification/ic_export.svg" />,
      title,
    };
  }
  if (notification?.function_id === FUNCTIONS.payment_order?.id) {
    return {
      avatar: <img style={{ width: 30 }} alt={title} src="/assets/icons/notification/ic_receipt.svg" />,
      title,
    };
  }
  return {
    avatar: <img alt={title} src="/assets/icons/notification/ic_package.svg" />,
    title,
  };
}
